/** @jsxImportSource @emotion/react */
import React, { Suspense, memo, useMemo, FC } from 'react'
import { Route, Switch } from 'react-router-dom'
// @ts-ignore
import { CSSTransition } from 'react-transition-group'
import Spinner from '@signal/components/baseline/Spinner'
import { useStandardDynamicPathReplacementHook } from '@signal/core/router/redirect'
// @ts-ignore
import withPage from '@signal/hoc/baseline/with-page'
import withPublicPage from '@signal/hoc/baseline/with-public-page'
import renderIf from '@signal/utils/renderIf'
import { TransitionContainer } from './App.styles'
import './route-transitions.css'
import { StartAppPropsType } from './types'
import { AccountGuidErrorBoundary } from '@signal/components/AccountGuidErrorBoundary'
import { AnyReactComponentType } from '@signal/utils/types'

const routeWrapper = (PageComponent: React.ComponentType<unknown>) =>
  memo(props => (
    <Suspense fallback={<Spinner size={250} />}>
      <PageComponent {...props} />
    </Suspense>
  ))

const TransitionWrapper = memo(
  ({ children }: { children?: React.ReactNode }) => (
    <TransitionContainer id="app-transition-container">
      <CSSTransition classNames="fade" timeout={300}>
        {children}
      </CSSTransition>
    </TransitionContainer>
  )
)

const NoTransitionWrapper = memo(
  ({ children }: { children?: React.ReactNode }) => <>{children}</>
)

const getRouteKey = (value: unknown | unknown[]): string =>
  Array.isArray(value) ? value.join('-') : `${value}`

const WrappedRoute: React.FC<{
  children?: React.ReactNode
  transitionComponent: AnyReactComponentType
}> = ({ transitionComponent: TransitionComponent, children }) => (
  <Route
    render={({ location }) => (
      <TransitionComponent>
        <Switch location={location}>{children}</Switch>
      </TransitionComponent>
    )}
  />
)

/*
  Baseline-derived apps should use this as the root of their application
  This app renders routes defined in routes.jsx.  Each route is expected to be a page from
  @purecarslabs/signal-layouts/baseline/Page.jsx,
  which gets a header and footer from the @purecarslabs/signal-hoc/baseline/withPage hoc.
  Feel free to modify the page layout or header/footer to suit your purposes (or define a sidebar instead of a
  header, etc by using a different HOC or augmenting that one.
*/
const App: FC<StartAppPropsType> = ({
  ErrorPage,
  appRoutes,
  appMinWidth,
  appName,
  appSupportedQP,
  loadPermissions,
  menuBaseUrl,
  noTransitions,
  standardRedirect,
  useDynamicPathReplacementHook,
  public: isPublic,
  PublicHeader,
  PublicFooter,
  publicHeaderHeight,
  publicFooterHeight
}) => {
  const TransitionWrapperUse = noTransitions
    ? NoTransitionWrapper
    : TransitionWrapper

  const standardDynamicPathReplacementHook = useStandardDynamicPathReplacementHook.bind(
    null,
    appRoutes
  )
  if (!useDynamicPathReplacementHook) {
    useDynamicPathReplacementHook = standardDynamicPathReplacementHook
  }

  const { standardHeaderProps, standardLoaderProps } = useMemo(
    () => ({
      standardHeaderProps: {
        appMinWidth,
        appName,
        appSupportedQP,
        standardRedirect,
        baseUrl: menuBaseUrl,
        useDynamicPathReplacementHook
      },
      standardLoaderProps: {
        appMinWidth,
        baseUrl: menuBaseUrl,
        loadPermissions
      }
    }),
    [
      appName,
      appMinWidth,
      appSupportedQP,
      menuBaseUrl,
      loadPermissions,
      standardRedirect,
      useDynamicPathReplacementHook
    ]
  )

  const routeMarkup = useMemo(
    () =>
      appRoutes.map(
        ({
          path,
          component,
          title,
          subtitle,
          hideChrome,
          hideFooter,
          hideMenu,
          routeRedirect
        }) => {
          const PageComponent = isPublic
            ? withPublicPage({
                PageComponent: routeWrapper(component),
                PublicHeader,
                PublicFooter,
                publicHeaderHeight,
                publicFooterHeight,
                appMinWidth,
                headerProps: { appName, title, subtitle },
                hideChrome,
                hideFooter
              })
            : withPage({
                PageComponent: routeWrapper(component),
                headerProps: {
                  ...standardHeaderProps,
                  routeRedirect,
                  title,
                  subtitle,
                  hideChrome,
                  hideFooter,
                  hideMenu
                },
                loaderProps: {
                  ...standardLoaderProps,
                  appMinWidth,
                  hideChrome,
                  hideFooter,
                  hideMenu
                }
              })

          return (
            <Route
              exact
              key={getRouteKey(path)}
              path={path}
              component={PageComponent}
            />
          )
        }
      ),
    [
      isPublic,
      PublicHeader,
      PublicFooter,
      publicHeaderHeight,
      publicFooterHeight,
      appName,
      appMinWidth,
      appRoutes,
      standardHeaderProps,
      standardLoaderProps
    ]
  )

  if (isPublic) {
    return (
      <WrappedRoute transitionComponent={TransitionWrapperUse}>
        {renderIf(Boolean(ErrorPage), () => (
          <Route
            path="/error"
            component={ErrorPage as React.ComponentType<unknown>}
          />
        ))}
        {routeMarkup}
      </WrappedRoute>
    )
  }

  return (
    <AccountGuidErrorBoundary redirectPath={standardRedirect}>
      <WrappedRoute transitionComponent={TransitionWrapperUse}>
        {renderIf(Boolean(ErrorPage), () => (
          <Route
            path="/:accountId/error"
            component={withPage({
              PageComponent: ErrorPage as React.ComponentType<unknown>,
              headerProps: standardHeaderProps,
              loaderProps: standardLoaderProps
            })}
          />
        ))}
        {renderIf(Boolean(ErrorPage), () => (
          <Route
            path="/error"
            component={ErrorPage as React.ComponentType<unknown>}
          />
        ))}
        {routeMarkup}
      </WrappedRoute>
    </AccountGuidErrorBoundary>
  )
}

export default memo(App)
